import type { Rows } from "./store";

/**
 * SEED DATA — the EXACT shape of the database: one `FrontOfficeSettings` row per
 * language, holding flat keys plus a `typosV2` object indexed by typology.
 *
 * There is therefore NO global "typology layer": every language carries its own
 * typology overrides. That is what makes four sources for a single value — and
 * why the panel always shows provenance.
 *
 * These values disappear the day the panel reads `GET /settings/fo`. They are
 * chosen to EXPOSE the tricky cases rather than to look nice:
 *   — the exhibitor title is overridden in French only (typology outranks language),
 *   — a boolean override is set to `false`, hence inert (the product defect).
 *
 * User-facing strings stay in French: the product is French.
 */

export const DEFAULT_LANGUAGE = 1;

export const LANGUAGES = [
  { id: 1, code: "fr-FR", name: "Français", short: "FR" },
  { id: 2, code: "en-GB", name: "Anglais", short: "EN" },
  { id: 3, code: "es-ES", name: "Espagnol", short: "ES" },
] as const;

export const TYPOS = [
  { id: null, name: "Tous les participants" },
  { id: 3, name: "Exposant" },
  { id: 4, name: "Intervenant" },
] as const;

export const SEED: Rows = {
  1: {
    FO_MEETING_V2_EVENT_NAME: "Salon Tech 2026",
    FO_MEETING_V2_EVENT_LOGO_TEXT: "ST",
    FO_MEETING_V2_SIGNOUT_LABEL: "Se déconnecter",
    FO_MEETING_V2_BRAND_COLOR: "#0f4c81",
    FO_MEETING_V2_PAGE_COLOR: "#fbfbfa",
    FO_MEETING_V2_HOME_TITLE: "Bonjour Camille",
    FO_MEETING_V2_HOME_SUBTITLE:
      "Votre inscription n'est pas encore finalisée.",
    FO_MEETING_V2_HOME_CTA: true,
    FO_MEETING_V2_HOME_CTA_TEXT: "Poursuivre mon inscription",
    FO_MEETING_V2_LOGIN_TITLE: "Connexion",
    FO_MEETING_V2_LOGIN_INTRO:
      "Utilisez l'adresse avec laquelle vous vous êtes inscrit.",
    FO_MEETING_V2_LOGIN_EMAIL_LABEL: "Adresse email",
    FO_MEETING_V2_LOGIN_PASSWORD_LABEL: "Mot de passe",
    FO_MEETING_V2_LOGIN_SUBMIT: "Se connecter",
    FO_MEETING_V2_LOGIN_FORGOT_SHOW: true,
    FO_MEETING_V2_LOGIN_FORGOT_LABEL: "Mot de passe oublié ?",
    FO_MEETING_V2_FOOTER_SHOW: true,
    FO_MEETING_V2_FOOTER_ORGANIZER: "Gayakoa Events",
    FO_MEETING_V2_FOOTER_DATES: "Du 14 au 16 avril 2026, de 9h à 18h",
    FO_MEETING_V2_EVENT_PLACE:
      "Palais des Congrès\n2 place de la Porte Maillot\n75017 Paris",
    FO_MEETING_V2_FOOTER_CONTACT:
      "assistance@salon-tech-2026.fr\n01 23 45 67 89",
    FO_MEETING_V2_FOOTER_MAP_URL:
      "https://www.google.com/maps/place/Palais+des+Congr%C3%A8s+de+Paris/@48.8783,2.2833,17z",
    FO_MEETING_V2_FOOTER_NOTICE:
      "Salon Tech 2026 est organisé par Gayakoa Events, SAS au capital de 50 000 € — RCS Paris 512 345 678. Mentions légales et politique de confidentialité sur salon-tech-2026.fr.",
    typosV2: {
      // An exhibitor does not attend the same event: their home screen talks setup.
      3: {
        FO_MEETING_V2_HOME_TITLE: "Bonjour Camille, espace exposant",
        FO_MEETING_V2_HOME_SUBTITLE:
          "Montage le lundi 13 avril à partir de 14h00.",
        // Switched OFF for exhibitors, and it genuinely is: an exhibitor has
        // nothing to complete, so the call to action would send them nowhere.
        // The reference implementation skips this override — see `cascade.ts`.
        FO_MEETING_V2_HOME_CTA: false,
      },
    },
  },

  2: {
    FO_MEETING_V2_HOME_TITLE: "Hello Camille",
    FO_MEETING_V2_HOME_SUBTITLE: "Your registration is not complete yet.",
    FO_MEETING_V2_HOME_CTA_TEXT: "Continue my registration",
    // Deliberately NO typology override in English: an English-speaking exhibitor
    // will therefore get the French text above. That is the product's behaviour.
  },

  3: {
    FO_MEETING_V2_HOME_TITLE: "Hola Camille",
  },
};
